import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { AppComponent } from '../../app.component';
import { BaseService } from '../../shared/services/base.service';

import { Dealer } from '../../shared/models/dealer.interface';
import { User } from '../../shared/models/user.interface';
import { DealerEngagement, DealerEngagementResult } from '../../reports/models/dealer.engagement.interface';
import { DealerLicensure, DealerLicensureResult } from '../../reports/models/dealer.licensure.interface';
import { Region } from '../../shared/models/region.interface';
import { Router } from '@angular/router';
import { DealerContact, DealerLContactResult } from '../../reports/models/dealer.contact.interface';
import { DealerLogin, DealersLastestLogin } from '../../reports/models/dealer.latest.login.interface';

@Injectable()

export class DealerService extends BaseService {

  constructor(private appComponent: AppComponent, protected http: HttpClient, router: Router) {
    super(http, router);
  }

  getList(): Observable<Dealer[]> {
    return this.httpGet<Dealer[]>("/dealer");
  }

  searchDealers(searchText: string): Observable<Dealer[]> {
    let params = new HttpParams();
    if (searchText != null) params = params.append('searchText', searchText);
    return this.httpGet<Dealer[]>("/dealer?" + params);
  }

  getDealerDetails(): Observable<Dealer> {
    return this.httpGet<Dealer>("/dealer/detail");
  }

  getDealer(dealerId): Observable<Dealer> {
    return this.httpGet<Dealer>("/dealer/" + dealerId);
  }

  getDealerLogo(): Observable<Blob> {
    return this.httpGetFile("/dealer/logo");
  }

  saveDealer(dealer: Dealer): Observable<Dealer> {
    if (dealer.dealer_ID == null)
      return this.httpPost<Dealer>("/dealer/", dealer);
    else
      return this.httpPut<Dealer>("/dealer/" + dealer.dealer_ID, dealer);
  }

  getStates(): Observable<string[]> {
    return this.httpGet<string[]>("/admin/states");
  }

  getRegions(): Observable<Region[]> {
    return this.httpGet<string[]>("/admin/regions");
  }

  getUsers(dealerId: number): Observable<User[]> {
    return this.httpGet<User[]>("/dealer/" + dealerId + "/users");
  }

  searchUsers(dealerId, searchText): Observable<User[]> {
    let params = new HttpParams();
    if (searchText != null) params = params.append('searchText', searchText);
    return this.httpGet<User[]>("/dealer/" + dealerId + "/users?" + params);
  }

  toggleUserStatus(userId: string, dealerId: number): Observable<User> {
    let url = "/user/toggle/" + userId;
    if (dealerId) url += '?dealerId=' + dealerId;

    return this.httpPut<User>(url, null);
  }

  toggleStatus(id: number): Observable<Dealer> {
    return this.httpPut<Dealer>("/dealer/" + id + '/toggleStatus', null);
  }

  getDealerContactExport(bidLog: DealerContact, format: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('format', format)
      .append('timezone', timezone.toString());

    return this.httpPostFile("/dealer/dealerContact?" + params.toString(), bidLog);
  }

  getDealerContactReport(bidLog: DealerContact): Observable<DealerLContactResult> {
    return this.httpPost<DealerLContactResult>("/dealer/dealerContact", bidLog);
  }

  getDealerEngagementExport(dealerEngagement: DealerEngagement, format: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('format', format)
      .append('timezone', timezone.toString());

    return this.httpPostFile("/dealer/dealerEngagement?" + params.toString(), dealerEngagement);
  }

  getDealerEngagementReport(dealerEngagement: DealerEngagement): Observable<DealerEngagementResult> {
    return this.httpPost<DealerEngagementResult>("/dealer/dealerEngagement", dealerEngagement);
  }

  getDealerLicensureReport(dealerId: number): Observable<DealerLicensureResult> {
    let params = new HttpParams()
      .append('dealerId', dealerId ? dealerId.toString() : null);

    return this.httpPost<DealerLicensureResult>("/dealer/dealerLicensure?" +  params.toString(), null)
  } 

  getDealerLicensureExport(dealerId: number, format: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('dealerId', dealerId ? dealerId?.toString() :null)
      .append('format', format)
      .append('timezone', timezone.toString());

    return this.httpPostFile("/dealer/dealerLicensure?" + params.toString(), null);
  }

  getDealerLoginReport(parameters: DealerLogin): Observable<DealersLastestLogin> {
    let params = new HttpParams()
      .append('dealer_Id', parameters.dealer_ID ? parameters.dealer_ID.toString() : null)
      .append('status', parameters.status !== null ? parameters.status.toString() : null);

    return this.httpPost<DealersLastestLogin>("/dealer/dealerLogin?" + params.toString(), null)
  }

  getDealerLoginExport(parameters: DealerLogin, format: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .append('dealerId', parameters.dealer_ID ? parameters.dealer_ID.toString() : null)
      .append('status', parameters.status ? parameters.status.toString() : null)
      .append('format', format)
      .append('timezone', timezone.toString());

    return this.httpPostFile("/dealer/dealerLogin?" + params.toString(), null);
  }

  exportDealers(): Observable<Blob> {
    return this.httpPostFile("/export/dealers", null);
  }
}
